// @ts-nocheck

// 🚀 V166.40.9: MessagesDataContext - COMPLETE FIX
// Fixes: JSON parsing errors, real-time unread counts, typing indicators, back navigation
// Uses shared auth + REST API (no Multiple GoTrueClient warning)

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabaseGuard';
import { useAuth } from '../hooks/useAuth';
import { getBlockedUserIds } from '../services/friends/friendRequestService'; // V168.BLOCK: Filter blocked users from chat list
import { useNetworkData } from './NetworkDataContext'; // V168.LOADING: Get user data for conversations
import { getPublicUserV1ByIds } from '../services/publicUsersV1Service';
import {
  subscribeToMessagesChannel,
  unsubscribeFromMessagesChannel,
  type MessagesChannelFactory
} from '@/realtime/messagesChannel';

// VCHAT-FINAL-03: Helper functions for user data resolution
/**
 * Normalize photo_urls which can be JSON string, array, or null
 */
function normalizePhotoUrls(photo_urls: any): string[] {
  if (!photo_urls) return [];

  // Already an array
  if (Array.isArray(photo_urls)) {
    return photo_urls.filter(url => typeof url === 'string' && url.length > 0);
  }

  // JSON string - try to parse
  if (typeof photo_urls === 'string') {
    try {
      const parsed = JSON.parse(photo_urls);
      if (Array.isArray(parsed)) {
        return parsed.filter(url => typeof url === 'string' && url.length > 0);
      }
    } catch (e) {
      // Not valid JSON, might be a single URL
      if (photo_urls.startsWith('http')) {
        return [photo_urls];
      }
    }
  }

  return [];
}

/**
 * Resolve user name from DB row (uses correct schema: name, google_name)
 */
function resolveUserName(row: any): string {
  return row?.name || 'Unknown User';
}

/**
 * Resolve user avatar from DB row (uses correct schema: photo_urls, profile_image_public_id)
 */
function resolveUserAvatar(row: any): string | null {
  // First try photo_urls array
  const urls = normalizePhotoUrls(row?.photo_urls);
  if (urls.length > 0) return urls[0];

  // Fallback: Build URL from profile_image_public_id
  const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
  const publicId = row?.profile_image_public_id;

  if (cloudName && publicId) {
    // Cloudinary URL without extension (Cloudinary auto-detects format)
    return `https://res.cloudinary.com/${cloudName}/image/upload/${publicId}`;
  }

  return null;
}

/** Sort/compare time for merge decisions (matches chat list ordering). */
function getConversationSortTime(c: any): number {
  return new Date(c?.timestamp || c?.updated_at || c?.created_at || 0).getTime();
}

function hasChatIdentity(c: any): boolean {
  return !!(c?.user1_id && c?.user2_id);
}

function hasOtherParticipant(c: any): boolean {
  const id = c?.otherUserId;
  return typeof id === 'string' && id.length > 0;
}

function hasResolvedDisplayName(c: any): boolean {
  const n = c?.name;
  if (n == null || typeof n !== 'string') return false;
  const t = n.trim();
  if (!t) return false;
  if (t === 'Unknown User' || t === 'Loading...') return false;
  return true;
}

function hasAvatarHint(c: any): boolean {
  return !!(c?.photoUrl || c?.userPhoto);
}

/** Higher = more usable for chat list display. */
function hydrationScore(c: any): number {
  let s = 0;
  if (hasChatIdentity(c)) s += 4;
  if (hasOtherParticipant(c)) s += 3;
  if (hasResolvedDisplayName(c)) s += 2;
  if (hasAvatarHint(c)) s += 1;
  return s;
}

function isPartialConversationRow(c: any): boolean {
  return !hasChatIdentity(c) || !hasOtherParticipant(c);
}

/**
 * Decide when server/loaded row should replace local optimistic row.
 * Hydration beats placeholder freshness when timestamps tie or existing is partial.
 */
function shouldReplaceWithServer(existing: any, incoming: any): boolean {
  const tEx = getConversationSortTime(existing);
  const tIn = getConversationSortTime(incoming);

  if (tIn > tEx) return true;

  if (tIn < tEx) {
    return (
      isPartialConversationRow(existing) &&
      hasChatIdentity(incoming) &&
      hasOtherParticipant(incoming)
    );
  }

  // Equal timestamps
  if (hydrationScore(incoming) > hydrationScore(existing)) return true;
  if (isPartialConversationRow(existing) && hasChatIdentity(incoming) && hasOtherParticipant(incoming)) {
    return true;
  }
  return false;
}

interface Message {
  id: string;
  chat_id: string;
  sender_id: string;
  content: string;
  created_at: string;
  read_at?: string;
}

interface Conversation {
  id: string;
  user1_id: string;
  user2_id: string;
  chat_type: string;
  created_at: string;
  updated_at: string;
  name?: string;
  photoUrl?: string | null; // V168.LOADING: Add photo URL
  lastMessage?: string;
  timestamp?: string;
  unreadCount?: number;
  otherUserId?: string;
  otherUser?: any;
}

interface MessagesDataContextType {
  conversations: Conversation[];
  loadingConversations: boolean;
  /** True after the first conversations fetch completes (including empty []). Survives KeepAlive late mounts. */
  conversationsHydrated: boolean;
  errorConversations: string | null;
  loadConversations: (forceRefresh?: boolean) => Promise<void>;

  getMessages: (chatId: string) => Message[];
  loadMessages: (chatId: string, forceRefresh?: boolean) => Promise<void>;
  sendMessage: (chatId: string, content: string) => Promise<void>;

  getUnreadCount: (chatId: string) => number;
  markAsRead: (chatId: string) => Promise<void>;
  totalUnreadCount: number;

  subscribeToChat: (chatId: string) => void;
  unsubscribeFromChat: (chatId: string) => void;

  // 🚀 V166.40.9: Typing indicators
  typingUsers: Record<string, string[]>;
  setUserTyping: (chatId: string, userId: string, isTyping: boolean) => void;

  // Chat list UX: current open chat (so real-time doesn't increment unread when user is in chat)
  setCurrentChatId: (chatId: string | null) => void;
}

// Safe no-op fallback for when provider is not mounted (e.g., unauth routes)
const noopMessagesContext: MessagesDataContextType = {
  conversations: [],
  loadingConversations: false,
  conversationsHydrated: false,
  errorConversations: null,
  loadConversations: async () => { },
  getMessages: () => [],
  loadMessages: async () => { },
  sendMessage: async () => { },
  getUnreadCount: () => 0,
  markAsRead: async () => { },
  totalUnreadCount: 0,
  subscribeToChat: () => { },
  unsubscribeFromChat: () => { },
  typingUsers: {},
  setUserTyping: () => { },
  setCurrentChatId: () => { },
};

const MessagesDataContext = createContext<MessagesDataContextType | undefined>(undefined);

export const useMessagesData = () => {
  const context = useContext(MessagesDataContext);
  if (!context) {
    if (import.meta.env.DEV) {
      console.warn('⚠️ useMessagesData called outside MessagesDataProvider. Returning no-op context.');
    }
    return noopMessagesContext;
  }
  return context;
};

// 🚀 V166.40.9: Fixed REST API client with proper JSON handling
class SupabaseRestClient {
  private baseUrl: string;
  private anonKey: string;

  constructor() {
    const url = import.meta.env.VITE_SUPABASE_URL?.trim();
    const key = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim();
    if (!url || !key) {
      throw new Error('[BlissNet] Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY');
    }
    this.baseUrl = url;
    this.anonKey = key;
  }

  private async getAuthToken(): Promise<string | null> {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  }

  async query(table: string, options: {
    select?: string;
    eq?: Record<string, any>;
    is?: Record<string, any>;
    order?: { column: string; ascending: boolean };
    limit?: number;
    count?: 'exact' | 'planned' | 'estimated';
    head?: boolean;
  } = {}) {
    const token = await this.getAuthToken();
    if (!token) {
      throw new Error('No auth token available');
    }

    let url = `${this.baseUrl}/rest/v1/${table}`;
    const params = new URLSearchParams();

    if (options.select) {
      params.append('select', options.select);
    }

    if (options.eq) {
      Object.entries(options.eq).forEach(([key, value]) => {
        params.append(key, `eq.${value}`);
      });
    }

    if (options.is) {
      Object.entries(options.is).forEach(([key, value]) => {
        params.append(key, `is.${value}`);
      });
    }

    if (options.order) {
      params.append('order', `${options.order.column}.${options.order.ascending ? 'asc' : 'desc'}`);
    }

    if (options.limit !== undefined) {
      params.append('limit', String(options.limit));
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    const headers: Record<string, string> = {
      'apikey': this.anonKey,
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    if (options.count) {
      headers['Prefer'] = `count=${options.count}`;
    }

    const response = await fetch(url, {
      method: options.head ? 'HEAD' : 'GET',
      headers
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw error;
    }

    const count = options.count ? parseInt(response.headers.get('Content-Range')?.split('/')[1] || '0') : null;

    // 🚀 V166.40.9: Fix - Don't parse JSON for HEAD requests
    const data = options.head ? null : await response.json();

    return { data, count, error: null };
  }

  async insert(table: string, data: any) {
    const token = await this.getAuthToken();
    if (!token) {
      throw new Error('No auth token available');
    }

    const url = `${this.baseUrl}/rest/v1/${table}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'apikey': this.anonKey,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw error;
    }

    const result = await response.json();
    return { data: Array.isArray(result) ? result[0] : result, error: null };
  }

  async update(table: string, data: any, filters: Record<string, any>) {
    const token = await this.getAuthToken();
    if (!token) {
      throw new Error('No auth token available');
    }

    let url = `${this.baseUrl}/rest/v1/${table}?`;
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value === null) {
        params.append(key, 'is.null');
      } else {
        params.append(key, `eq.${value}`);
      }
    });

    url += params.toString();

    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'apikey': this.anonKey,
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        // 🚀 V166.40.9: Fix - Don't request representation (causes JSON parsing issues)
        'Prefer': 'return=minimal'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw error;
    }

    // 🚀 V166.40.9: Fix - Don't parse JSON for minimal return
    return { error: null };
  }
}

export const MessagesDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth() as any;
  const { getUser } = useNetworkData(); // V168.LOADING: Get user data for conversations

  const restClient = useRef(new SupabaseRestClient());

  // V166.12: Removed excessive console logs (production cleanup)

  // State
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [conversationsHydrated, setConversationsHydrated] = useState(false);
  const [errorConversations, setErrorConversations] = useState<string | null>(null);

  const [messagesByChat, setMessagesByChat] = useState<Record<string, Message[]>>({});
  const [unreadCountsByChat, setUnreadCountsByChat] = useState<Record<string, number>>({});
  const [readyUserId, setReadyUserId] = useState<string | null>(null);
  const isAuthReady = !!user?.id && readyUserId === user.id;

  // Chat list UX: which chat is open (so real-time doesn't increment unread when user is viewing it)
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const currentChatIdRef = useRef<string | null>(null);
  currentChatIdRef.current = currentChatId;

  // 🚀 V166.40.9: Typing indicators state
  const [typingUsers, setTypingUsers] = useState<Record<string, string[]>>({});
  const typingTimeouts = useRef<Record<string, NodeJS.Timeout>>({});

  // Deduplication guards
  const conversationsLoadingRef = useRef(false);
  const needsReloadRef = useRef(false);
  /** True after first conversations fetch completes — background refreshes must not flip loading UI */
  const conversationsHydratedRef = useRef(false);
  const messagesLoadingRef = useRef<Record<string, boolean>>({});
  const subscriptionsRef = useRef<Record<string, any>>({});
  const globalSubscriptionRef = useRef<any>(null);
  const isSubscribing = useRef<boolean>(false);
  const authReadyUserIdRef = useRef<string | null>(null);

  // Check if read_at column exists
  const [hasReadAtColumn, setHasReadAtColumn] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user?.id) return;

    const checkReadAtColumn = async () => {
      try {
        await restClient.current.query('messages', {
          select: 'read_at',
          limit: 0
        });

        setHasReadAtColumn(true);
      } catch (error: any) {
        if (error.message?.includes('read_at') || error.code === 'PGRST204') {
          console.warn('⚠️ V166.40.9: read_at column does not exist - mark-as-read disabled');
          setHasReadAtColumn(false);
        } else {
          setHasReadAtColumn(true);
        }
      }
    };

    checkReadAtColumn();
  }, [user?.id]);

  // 🚀 V166.40.9: Typing indicator handler (defined early to avoid dependency issues)
  const setUserTyping = useCallback((chatId: string, userId: string, isTyping: boolean) => {
    if (isTyping) {
      setTypingUsers(prev => {
        const chatTyping = prev[chatId] || [];
        if (!chatTyping.includes(userId)) {
          return {
            ...prev,
            [chatId]: [...chatTyping, userId]
          };
        }
        return prev;
      });

      // Clear existing timeout
      if (typingTimeouts.current[`${chatId}-${userId}`]) {
        clearTimeout(typingTimeouts.current[`${chatId}-${userId}`]);
      }

      // Set timeout to remove typing after 3 seconds
      typingTimeouts.current[`${chatId}-${userId}`] = setTimeout(() => {
        setTypingUsers(prev => ({
          ...prev,
          [chatId]: (prev[chatId] || []).filter(id => id !== userId)
        }));
      }, 3000);
    } else {
      setTypingUsers(prev => ({
        ...prev,
        [chatId]: (prev[chatId] || []).filter(id => id !== userId)
      }));

      if (typingTimeouts.current[`${chatId}-${userId}`]) {
        clearTimeout(typingTimeouts.current[`${chatId}-${userId}`]);
      }
    }
  }, []);

  // Load conversations
  const loadConversations = useCallback(async (forceRefresh = false) => {
    if (!user?.id) {
      return;
    }

    // P2 traffic: never overlap full reloads — coalesce into one follow-up
    if (conversationsLoadingRef.current) {
      needsReloadRef.current = true;
      return;
    }

    conversationsLoadingRef.current = true;
    // Only show loading on the first hydrate — background refreshes keep existing UI
    if (!conversationsHydratedRef.current) {
      setLoadingConversations(true);
    }
    setErrorConversations(null);

    try {
      // V166.12: Removed excessive console logs (production cleanup)

      // 🚀 V167: Include last_message and last_message_at for reliable last message display
      // 🚀 VCHAT-FINAL-04-DIAG-01: Filter out corrupted chats (missing user references)
      const { data: chatsAsUser1 } = await restClient.current.query('chats', {
        select: 'id,user1_id,user2_id,chat_type,created_at,updated_at,last_message,last_message_at',
        eq: { user1_id: user.id },
        is: { 'metadata->>corrupted': null }, // Exclude corrupted chats
        order: { column: 'last_message_at', ascending: false }
      });

      const { data: chatsAsUser2 } = await restClient.current.query('chats', {
        select: 'id,user1_id,user2_id,chat_type,created_at,updated_at,last_message,last_message_at',
        eq: { user2_id: user.id },
        is: { 'metadata->>corrupted': null }, // Exclude corrupted chats
        order: { column: 'last_message_at', ascending: false }
      });

      const allChats = [
        ...(chatsAsUser1 || []),
        ...(chatsAsUser2 || [])
      ];

      const uniqueChats = Array.from(
        new Map(allChats.map(chat => [chat.id, chat])).values()
      );

      // V168.BLOCK: Load blocked user IDs and filter them out
      const blockedUserIds = await getBlockedUserIds(user.id);
      const blockedSet = new Set(blockedUserIds);
      console.log('🔒 BLOCK: Filtering', blockedUserIds.length, 'blocked users from chat list');

      // Chat list UX: batch fetch all other-user profiles so names/avatars appear without refresh
      const otherUserIds = [...new Set(
        uniqueChats
          .map(chat => (chat.user1_id === user.id ? chat.user2_id : chat.user1_id))
          .filter(id => !blockedSet.has(id))
      )];
      const usersById = new Map<string, { id: string; name?: string; google_name?: string; photo_urls?: any; profile_image_public_id?: string }>();
      if (otherUserIds.length > 0) {
        try {
          const usersBatch = await getPublicUserV1ByIds(otherUserIds);
          (usersBatch || []).forEach(u => usersById.set(u.id, u));
        } catch (batchErr) {
          console.warn('⚠️ Chat list: batch profile fetch failed', batchErr);
        }
      }

      // P2 traffic: one batched unread GET instead of per-chat HEAD /messages
      // 1:1 chats only (user1/user2) — same semantics as prior sender_id=otherUserId + read_at is null
      const eligibleChatIds = uniqueChats
        .filter(chat => {
          const otherId = chat.user1_id === user.id ? chat.user2_id : chat.user1_id;
          return !blockedSet.has(otherId);
        })
        .map(chat => chat.id);

      const unreadByChat: Record<string, number> = {};
      if (hasReadAtColumn && eligibleChatIds.length > 0) {
        try {
          const { data: unreadRows, error: unreadError } = await supabase
            .from('messages')
            .select('chat_id')
            .in('chat_id', eligibleChatIds)
            .neq('sender_id', user.id)
            .is('read_at', null);

          if (unreadError) {
            console.warn('⚠️ V166.40.9: Could not get unread counts (batch):', unreadError);
          } else {
            for (const row of unreadRows || []) {
              if (!row?.chat_id) continue;
              unreadByChat[row.chat_id] = (unreadByChat[row.chat_id] || 0) + 1;
            }
          }
        } catch (error) {
          console.warn('⚠️ V166.40.9: Could not get unread counts (batch):', error);
        }
      }

      const processedConversations: Conversation[] = [];

      for (const chat of uniqueChats) {
        const otherUserId = chat.user1_id === user.id ? chat.user2_id : chat.user1_id;

        // V168.BLOCK: Skip chats with blocked users
        if (blockedSet.has(otherUserId)) {
          console.log('🔒 BLOCK: Skipping chat with blocked user:', otherUserId);
          continue;
        }

        // 🚀 V167: Use last_message from chats table if available (more reliable)
        // Fall back to querying messages only if last_message is null
        let lastMessageContent = chat.last_message;
        let lastMessageTimestamp = chat.last_message_at || chat.updated_at || chat.created_at;

        if (!lastMessageContent) {
          // Fallback: Query messages if last_message is not set
          try {
            const { data: messages } = await restClient.current.query('messages', {
              select: 'content,created_at',
              eq: { chat_id: chat.id },
              order: { column: 'created_at', ascending: false },
              limit: 1
            });

            if (messages?.[0]) {
              lastMessageContent = messages[0].content;
              lastMessageTimestamp = messages[0].created_at;
            }
          } catch (error) {
            console.warn('⚠️ V167: Could not query last message:', error);
          }
        }

        const unreadCount = hasReadAtColumn ? (unreadByChat[chat.id] || 0) : 0;

        // Chat list UX: name/avatar from batch first, then getUser cache, then single-user fallback
        let userName = 'Unknown User';
        let userAvatar: string | null = null;
        const batchUser = usersById.get(otherUserId);
        if (batchUser) {
          userName = resolveUserName(batchUser);
          userAvatar = resolveUserAvatar(batchUser);
        } else if (getUser && otherUserId) {
          const otherUser = getUser(otherUserId);
          if (otherUser) {
            userName = resolveUserName(otherUser);
            userAvatar = resolveUserAvatar(otherUser);
          } else {
            try {
              const { data: userData } = await restClient.current.query('users_public_v1', {
                select: 'id,name:display_name,photo_urls,profile_image_public_id',
                eq: { id: otherUserId },
                limit: 1
              });
              if (userData?.[0]) {
                userName = resolveUserName(userData[0]);
                userAvatar = resolveUserAvatar(userData[0]);
              }
            } catch (error) {
              console.warn('[chat] User fetch failed for', otherUserId, error);
            }
          }
        } else if (otherUserId) {
          try {
            const { data: userData } = await restClient.current.query('users_public_v1', {
              select: 'id,name:display_name,photo_urls,profile_image_public_id',
              eq: { id: otherUserId },
              limit: 1
            });
            if (userData?.[0]) {
              userName = resolveUserName(userData[0]);
              userAvatar = resolveUserAvatar(userData[0]);
            }
          } catch (error) {
            console.warn('[chat] User fetch failed for', otherUserId, error);
          }
        }

        processedConversations.push({
          ...chat,
          otherUserId,
          name: userName, // V168.LOADING: Add user name
          photoUrl: userAvatar, // V168.LOADING: Add user avatar
          lastMessage: lastMessageContent || 'Start chatting...',
          timestamp: lastMessageTimestamp || chat.created_at,
          unreadCount
        });

        setUnreadCountsByChat(prev => ({
          ...prev,
          [chat.id]: unreadCount
        }));
      }

      // V323.BUG3: Dedupe by otherUserId — prefer chat with message history over empty newer one
      const EMPTY_PLACEHOLDER = 'Start chatting...';
      const hasMessages = (c: { lastMessage?: string }) =>
        !!c.lastMessage && c.lastMessage !== EMPTY_PLACEHOLDER;
      const dedupedByOtherUser = new Map<string, typeof processedConversations[0]>();
      for (const conv of processedConversations) {
        const key = conv.otherUserId || conv.id;
        const existing = dedupedByOtherUser.get(key);
        if (!existing) {
          dedupedByOtherUser.set(key, conv);
        } else {
          const existingHasMessages = hasMessages(existing);
          const currentHasMessages = hasMessages(conv);
          const existingTs = new Date(existing.timestamp || 0).getTime();
          const currentTs = new Date(conv.timestamp || 0).getTime();
          let keepCurrent = false;
          if (existingHasMessages && !currentHasMessages) keepCurrent = false;
          else if (!existingHasMessages && currentHasMessages) keepCurrent = true;
          else keepCurrent = currentTs > existingTs;
          if (keepCurrent) dedupedByOtherUser.set(key, conv);
        }
      }
      const dedupedConversations = Array.from(dedupedByOtherUser.values());

      // 🚀 V167: When force refreshing, merge intelligently to preserve real-time updates
      // This prevents overwriting newer data that came from real-time subscriptions
      if (forceRefresh) {
        // Force refresh - merge with existing conversations, keeping the newer data
        setConversations(prev => {
          // V168.BLOCK: Filter out chats with blocked users from existing conversations
          const filteredPrev = prev.filter(conv => {
            const otherUserId = conv.user1_id === user.id ? conv.user2_id : conv.user1_id;
            return !blockedSet.has(otherUserId);
          });

          const merged = [...filteredPrev];

          dedupedConversations.forEach(newConv => {
            const existingIndex = merged.findIndex(c => c.id === newConv.id);

            if (existingIndex === -1) {
              // New conversation - add it
              merged.push(newConv);
            } else {
              const existing = merged[existingIndex];
              const replace = shouldReplaceWithServer(existing, newConv);

              console.log('🔔 V167: Force refresh merge (shouldReplaceWithServer):', {
                chatId: newConv.id,
                existingLastMessage: existing.lastMessage,
                existingTimestamp: existing.timestamp,
                newLastMessage: newConv.lastMessage,
                newTimestamp: newConv.timestamp,
                replace
              });

              if (replace) {
                merged[existingIndex] = newConv;
              } else {
                console.log('✅ V167: Keeping existing conversation (fresher or more hydrated):', {
                  chatId: existing.id,
                  lastMessage: existing.lastMessage,
                  timestamp: existing.timestamp
                });
              }
            }
          });

          // VCHAT-FINAL-03: Sort copy to prevent mutation bugs
          const sorted = [...merged].sort((a, b) => {
            const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
            const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
            return timeB - timeA; // Descending (newest first)
          });

          return sorted;
        });
      } else {
        // Normal load - merge with existing to preserve real-time updates
        setConversations(prev => {
          // V168.BLOCK: Filter out chats with blocked users from existing conversations
          const filteredPrev = prev.filter(conv => {
            const otherUserId = conv.user1_id === user.id ? conv.user2_id : conv.user1_id;
            return !blockedSet.has(otherUserId);
          });

          const merged = [...filteredPrev];

          dedupedConversations.forEach(newConv => {
            const existingIndex = merged.findIndex(c => c.id === newConv.id);

            if (existingIndex === -1) {
              // New conversation - add it
              merged.push(newConv);
            } else {
              const existing = merged[existingIndex];
              if (shouldReplaceWithServer(existing, newConv)) {
                merged[existingIndex] = newConv;
              }
            }
          });

          // VCHAT-FINAL-03: Sort copy to prevent mutation bugs
          const sorted = [...merged].sort((a, b) => {
            const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
            const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
            return timeB - timeA; // Descending (newest first)
          });

          return sorted;
        });
      }
      // V166.12: Removed excessive console logs (production cleanup)

    } catch (error: any) {
      // V193.1: Handle auth token errors gracefully (auth not ready yet)
      if (error.message?.includes('No auth token available')) {
        if (import.meta.env.DEV) {
          console.warn('⚠️ V193.1: Auth token not available yet, conversations will load when auth is ready');
        }
        setErrorConversations(null); // Don't show error to user, will retry when auth is ready
      } else {
        console.error('❌ V166.40.9: Error loading conversations:', error);
        setErrorConversations(error.message || 'Failed to load conversations');
      }
    } finally {
      conversationsHydratedRef.current = true;
      setConversationsHydrated(true);
      setLoadingConversations(false);
      conversationsLoadingRef.current = false;
      if (needsReloadRef.current) {
        needsReloadRef.current = false;
        void loadConversations(true);
      }
    }
  }, [user?.id, hasReadAtColumn, getUser]);

  const loadConversationsRef = useRef(loadConversations);
  loadConversationsRef.current = loadConversations;

  useEffect(() => {
    let cancelled = false;
    authReadyUserIdRef.current = null;
    setReadyUserId(null);

    if (!user?.id) return;

    const userId = user.id;

    const resolveAuth = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (cancelled || error || data?.session?.user?.id !== userId) {
          return;
        }
        authReadyUserIdRef.current = userId;
        setReadyUserId(userId);
      } catch (error) {
        console.warn('[messages-realtime] Failed resolving auth session', error);
      }
    };

    void resolveAuth();

    return () => {
      cancelled = true;
      authReadyUserIdRef.current = null;
    };
  }, [user?.id]);

  // 🚀 V166.40.9: Global real-time subscription for all messages (updates unread counts and last message)
  // V166.12: Removed excessive console logs, fixed chatlist update issue
  useEffect(() => {
    if (isSubscribing.current) return;
    if (!isAuthReady) return;
    if (!user?.id || authReadyUserIdRef.current !== user.id) return;

    const createMessagesChannel: MessagesChannelFactory = (channelUserId, onStatus) => supabase
      .channel(`messages:${channelUserId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages'
      }, async (payload) => {
        const newMessage = payload.new as Message;

        // 🚀 V166.40.9: Update conversation list with new message (ALWAYS update last message)
        setConversations(prev => {
          // 🚀 V166.40.9: Create new array reference to ensure React detects change
          const prevArray = [...prev];
          const conversationExists = prevArray.some(conv => conv.id === newMessage.chat_id);

          if (!conversationExists) {
            // 🚀 V166.40.9: Trigger a reload if conversation doesn't exist
            setTimeout(() => {
              loadConversations(true);
            }, 1000);
            return prevArray; // Return new array reference
          }

          // 🚀 V166.40.9: Map to new array with updated conversation
          const updated = prevArray.map(conv => {
            if (conv.id === newMessage.chat_id) {
              const isFromOtherUser = newMessage.sender_id !== user.id;
              const newTimestamp = new Date(newMessage.created_at).getTime();
              const oldTimestamp = new Date(conv.timestamp || conv.created_at || 0).getTime();

              // 🚀 V167: CRITICAL FIX - Always update lastMessage and timestamp
              // This ensures messages sent while chatlist is open appear immediately
              // Also update last_message in chats table for consistency (for both own and other user messages)
              if (newTimestamp >= oldTimestamp || newMessage.sender_id === user.id) {
                console.log('🔔 V167: Real-time subscription updating conversation:', {
                  chatId: newMessage.chat_id,
                  lastMessage: newMessage.content,
                  timestamp: newMessage.created_at,
                  isOwnMessage: newMessage.sender_id === user.id
                });

                // Update chats table last_message field in background (non-blocking)
                // Update for all messages to ensure both users see the correct last message
                supabase
                  .from('chats')
                  .update({
                    last_message: newMessage.content,
                    last_message_at: newMessage.created_at
                  })
                  .eq('id', newMessage.chat_id)
                  .then(() => {
                    console.log('✅ V167: Chat updated via real-time subscription:', {
                      chatId: newMessage.chat_id,
                      lastMessage: newMessage.content
                    });
                  })
                  .catch(err => console.warn('⚠️ V167: Could not update chat last_message:', err));

                const isViewingThisChat = newMessage.chat_id === currentChatIdRef.current;
                const updatedConv = {
                  ...conv,
                  lastMessage: newMessage.content,
                  timestamp: newMessage.created_at,
                  updated_at: newMessage.created_at,
                  unreadCount: isFromOtherUser && !isViewingThisChat ? (conv.unreadCount || 0) + 1 : (isViewingThisChat ? 0 : conv.unreadCount)
                };
                return updatedConv;
              }
              return conv;
            }
            return conv;
          });

          // Sort by timestamp (most recent first) - ensure latest message is at top
          // 🚀 V166.40.9: Create new sorted array to ensure React detects change
          const sorted = [...updated].sort((a, b) => {
            const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
            const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
            return timeB - timeA; // Descending (newest first)
          });

          return sorted; // Return new array reference
        });

        // Update unread count cache only if user is NOT currently viewing this chat
        if (newMessage.sender_id !== user.id && newMessage.chat_id !== currentChatIdRef.current) {
          setUnreadCountsByChat(prev => ({
            ...prev,
            [newMessage.chat_id]: (prev[newMessage.chat_id] || 0) + 1
          }));
        }

        // 🚀 V166.42: CRITICAL FIX - Update message cache when message arrives while chat list is open
        // This ensures messages are in cache even if ChatConversation component isn't mounted
        try {
          const { ChatMessageCache } = await import('../services/ChatMessageCache');
          const cachedMessages = ChatMessageCache.getMessages(newMessage.chat_id, user.id);

          // Check if message already exists in cache
          const messageExists = cachedMessages.some(msg => msg.id === newMessage.id);
          if (!messageExists) {
            // Add new message to cache
            const transformedMessage = {
              id: newMessage.id,
              chat_id: newMessage.chat_id,
              sender_id: newMessage.sender_id,
              content: newMessage.content,
              created_at: newMessage.created_at,
              read: newMessage.is_read ?? newMessage.read ?? false,
              is_read: newMessage.is_read ?? newMessage.read ?? false,
              senderName: 'User', // Will be populated by component
              senderPhotoUrl: '',
              status: 'sent' as const,
              type: 'text' as const,
              metadata: {}
            };
            const updatedMessages = [...cachedMessages, transformedMessage];
            // VCHAT-FINAL-04: Sort copy to prevent mutation bugs
            const sorted = [...updatedMessages].sort((a, b) =>
              new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            );
            ChatMessageCache.saveMessages(newMessage.chat_id, sorted, user.id);
            // 🚀 V166.43: Only log in dev mode (reduces console noise)
            if (import.meta.env.DEV) {
              console.log('✅ V166.42-CACHE-UPDATE: Updated cache with message from global subscription', {
                chatId: newMessage.chat_id,
                messageId: newMessage.id,
                cacheSize: sorted.length
              });
            }
          }
        } catch (cacheError) {
          console.warn('⚠️ V166.42: Failed to update cache from global subscription', cacheError);
        }

        // 🚀 V166.40.9: Broadcast message to app so open conversations can update instantly
        try {
          window.dispatchEvent(new CustomEvent('chat-message-inserted', { detail: newMessage }));
        } catch (e) {
          console.warn('⚠️ V166.40.9: Failed to dispatch chat-message-inserted event', e);
        }
      })
      .subscribe(onStatus);

    isSubscribing.current = true;
    try {
      globalSubscriptionRef.current = subscribeToMessagesChannel(
        createMessagesChannel,
        user.id
      );
    } catch (error) {
      isSubscribing.current = false;
      throw error;
    }

    return () => {
      globalSubscriptionRef.current = null;
      unsubscribeFromMessagesChannel(createMessagesChannel);
      isSubscribing.current = false;
    };
  }, [user?.id, isAuthReady, loadConversations]);

  // 🚀 V167: Real-time subscription for chats table updates
  // This ensures chat list updates when last_message is updated
  useEffect(() => {
    if (!isAuthReady) return;
    if (!user?.id || authReadyUserIdRef.current !== user.id) return;

    const chatsUpdateChannel = supabase
      .channel(`chats-updates:${user.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'chats',
        filter: `user1_id=eq.${user.id}`
      }, async (payload) => {
        const updatedChat = payload.new as any;
        const oldChat = payload.old as any;

        // Only update if last_message or last_message_at changed
        if (updatedChat.last_message !== oldChat.last_message ||
          updatedChat.last_message_at !== oldChat.last_message_at) {
          console.log('🔔 V167: Chat updated via real-time:', {
            chatId: updatedChat.id,
            lastMessage: updatedChat.last_message,
            lastMessageAt: updatedChat.last_message_at
          });

          // Update conversation in list
          setConversations(prev => {
            const updated = prev.map(conv => {
              if (conv.id === updatedChat.id) {
                return {
                  ...conv,
                  lastMessage: updatedChat.last_message || conv.lastMessage,
                  timestamp: updatedChat.last_message_at || updatedChat.updated_at || conv.timestamp,
                  updated_at: updatedChat.updated_at || conv.updated_at
                };
              }
              return conv;
            });

            // Sort by timestamp (most recent first)
            return [...updated].sort((a, b) => {
              const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
              const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
              return timeB - timeA;
            });
          });
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'chats',
        filter: `user2_id=eq.${user.id}`
      }, async (payload) => {
        const updatedChat = payload.new as any;
        const oldChat = payload.old as any;

        // Only update if last_message or last_message_at changed
        if (updatedChat.last_message !== oldChat.last_message ||
          updatedChat.last_message_at !== oldChat.last_message_at) {
          console.log('🔔 V167: Chat updated via real-time (user2):', {
            chatId: updatedChat.id,
            lastMessage: updatedChat.last_message,
            lastMessageAt: updatedChat.last_message_at
          });

          // Update conversation in list
          setConversations(prev => {
            const updated = prev.map(conv => {
              if (conv.id === updatedChat.id) {
                return {
                  ...conv,
                  lastMessage: updatedChat.last_message || conv.lastMessage,
                  timestamp: updatedChat.last_message_at || updatedChat.updated_at || conv.timestamp,
                  updated_at: updatedChat.updated_at || conv.updated_at
                };
              }
              return conv;
            });

            // Sort by timestamp (most recent first)
            return [...updated].sort((a, b) => {
              const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
              const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
              return timeB - timeA;
            });
          });
        }
      })
      .subscribe();

    return () => {
      if (chatsUpdateChannel) {
        supabase.removeChannel(chatsUpdateChannel);
      }
    };
  }, [user?.id, isAuthReady]);

  // 🚀 V167: Real-time subscription for new chats - ensures new chats appear immediately
  useEffect(() => {
    if (!isAuthReady) return;
    if (!user?.id || authReadyUserIdRef.current !== user.id) return;

    // Subscribe to chats where user is user1
    const chatsChannel1 = supabase
      .channel(`chats-user1:${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chats',
        filter: `user1_id=eq.${user.id}`
      }, async (payload) => {
        const newChat = payload.new as any;

        // Check if conversation already exists
        setConversations(prev => {
          const exists = prev.some(conv => conv.id === newChat.id);
          if (exists) {
            return prev; // Already exists, don't add duplicate
          }

          // New chat - trigger a reload to get full conversation details
          setTimeout(() => {
            loadConversations(true);
          }, 500);

          return prev;
        });
      })
      .subscribe();

    // Subscribe to chats where user is user2
    const chatsChannel2 = supabase
      .channel(`chats-user2:${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chats',
        filter: `user2_id=eq.${user.id}`
      }, async (payload) => {
        const newChat = payload.new as any;

        // Check if conversation already exists
        setConversations(prev => {
          const exists = prev.some(conv => conv.id === newChat.id);
          if (exists) {
            return prev; // Already exists, don't add duplicate
          }

          // New chat - trigger a reload to get full conversation details
          setTimeout(() => {
            loadConversations(true);
          }, 500);

          return prev;
        });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(chatsChannel1);
      supabase.removeChannel(chatsChannel2);
    };
  }, [user?.id, isAuthReady, loadConversations]);

  // Load messages
  const loadMessages = useCallback(async (chatId: string, forceRefresh = false) => {
    if (!user?.id || !chatId) return;

    if (messagesLoadingRef.current[chatId] && !forceRefresh) {
      // V166.12: Removed excessive console logs (production cleanup)
      return;
    }

    messagesLoadingRef.current[chatId] = true;

    try {
      // V166.12: Removed excessive console logs (production cleanup)

      const { data: messages } = await restClient.current.query('messages', {
        select: '*',
        eq: { chat_id: chatId },
        order: { column: 'created_at', ascending: true }
      });

      setMessagesByChat(prev => ({
        ...prev,
        [chatId]: messages || []
      }));

      // V166.12: Removed excessive console logs (production cleanup)

    } catch (error) {
      console.error('❌ V166.40.9: Error loading messages:', error);
    } finally {
      messagesLoadingRef.current[chatId] = false;
    }
  }, [user?.id]);

  // Send message
  const sendMessage = useCallback(async (chatId: string, content: string) => {
    if (!user?.id || !chatId || !content.trim()) return;

    try {
      // V166.12: Removed excessive console logs (production cleanup)

      const { data } = await restClient.current.insert('messages', {
        chat_id: chatId,
        sender_id: user.id,
        content: content.trim(),
        created_at: new Date().toISOString()
      });

      setMessagesByChat(prev => ({
        ...prev,
        [chatId]: [...(prev[chatId] || []), data]
      }));

      // 🚀 V166.12: CRITICAL FIX - Immediately update conversation when sending message
      // This ensures chatlist updates instantly even before real-time subscription fires
      setConversations(prev => {
        const updated = prev.map(conv => {
          if (conv.id === chatId) {
            return {
              ...conv,
              lastMessage: content.trim(),
              timestamp: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
          }
          return conv;
        });

        // Sort by timestamp (most recent first)
        return [...updated].sort((a, b) => {
          const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
          const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
          return timeB - timeA;
        });
      });

    } catch (error) {
      console.error('❌ V166.40.9: Error sending message:', error);
      throw error;
    }
  }, [user?.id]);

  // Mark messages as read: optimistic update first so badge clears instantly, then server
  const markAsRead = useCallback(async (chatId: string) => {
    if (!user?.id || !chatId) return;

    // Optimistic: clear unread immediately so chat list badge updates without refresh
    setUnreadCountsByChat(prev => ({ ...prev, [chatId]: 0 }));
    setConversations(prev => prev.map(conv =>
      conv.id === chatId ? { ...conv, unreadCount: 0 } : conv
    ));

    if (hasReadAtColumn === false) return;

    try {
      const conversation = conversations.find(c => c.id === chatId);
      const otherUserId = conversation?.otherUserId;
      if (otherUserId) {
        await restClient.current.update('messages',
          { read_at: new Date().toISOString() },
          { chat_id: chatId, sender_id: otherUserId, read_at: null }
        );
      }
    } catch (error: any) {
      if (error.message?.includes('read_at') || error.code === 'PGRST204') {
        setHasReadAtColumn(false);
      } else {
        console.error('❌ V166.40.9: Error marking messages as read:', error);
      }
    }
  }, [user?.id, conversations, hasReadAtColumn]);

  const getMessages = useCallback((chatId: string): Message[] => {
    return messagesByChat[chatId] || [];
  }, [messagesByChat]);

  const getUnreadCount = useCallback((chatId: string): number => {
    return unreadCountsByChat[chatId] || 0;
  }, [unreadCountsByChat]);

  const totalUnreadCount = Object.values(unreadCountsByChat).reduce((sum, count) => sum + count, 0);

  const subscribeToChat = useCallback((chatId: string) => {
    if (!user?.id || !chatId) return;
    if (!isAuthReady || authReadyUserIdRef.current !== user.id) return;
    const subscriptionKey = `${user.id}:${chatId}`;
    if (subscriptionsRef.current[subscriptionKey]) {
      // V166.12: Removed excessive console logs (production cleanup)
      return;
    }

    const channel = supabase
      .channel(`chat-${chatId}:${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `chat_id=eq.${chatId}`
      }, (payload) => {
        // V166.12: Removed excessive console logs (production cleanup)
        const newMessage = payload.new as Message;

        setMessagesByChat(prev => ({
          ...prev,
          [chatId]: [...(prev[chatId] || []), newMessage]
        }));
      })
      .subscribe();

    subscriptionsRef.current[subscriptionKey] = channel;
  }, [user?.id, isAuthReady]);

  const unsubscribeFromChat = useCallback((chatId: string) => {
    const subscriptionKey = user?.id ? `${user.id}:${chatId}` : chatId;
    const channel = subscriptionsRef.current[subscriptionKey];
    if (channel) {
      // V166.12: Removed excessive console logs (production cleanup)
      supabase.removeChannel(channel);
      delete subscriptionsRef.current[subscriptionKey];
    }
  }, [user?.id]);

  // 🚀 V166.12: Listen for chat-message-inserted events to update conversations immediately
  // This ensures chatlist updates even if real-time subscription is delayed
  useEffect(() => {
    const handleMessageInserted = (evt: any) => {
      const newMessage = evt?.detail;
      if (!newMessage || !user?.id || !newMessage.chat_id) {
        console.warn('⚠️ V167: Invalid chat-message-inserted event:', { newMessage, userId: user?.id });
        return;
      }

      console.log('🔔 V167: chat-message-inserted event received:', {
        chatId: newMessage.chat_id,
        messageId: newMessage.id,
        content: newMessage.content,
        senderId: newMessage.sender_id,
        created_at: newMessage.created_at,
        isOwnMessage: newMessage.sender_id === user.id
      });

      // 🚀 V167: CRITICAL FIX - Update conversation immediately when message is sent
      // This is especially important when sending while chatlist is open
      setConversations(prev => {
        const prevArray = [...prev];
        const conversationExists = prevArray.some(conv => conv.id === newMessage.chat_id);

        if (!conversationExists) {
          console.log('🚀 V167.7: Conversation not found, creating placeholder entry:', newMessage.chat_id);

          // 🚀 V167.7: CRITICAL FIX - Create conversation entry immediately instead of waiting for reload
          // This ensures the chat appears in the list instantly when sending the first message
          // V168.LOADING: Try to get user data immediately
          let userName = newMessage.otherUserName || null; // V329.2: Use null so ChatList can distinguish "loading" from "resolved"
          let userAvatar = newMessage.otherUserAvatar || null;

          if (getUser && newMessage.otherUserId) {
            const otherUser = getUser(newMessage.otherUserId);
            if (otherUser) {
              userName = otherUser.name || userName;
              // photo_urls is an array, get first element
              userAvatar = Array.isArray(otherUser.photo_urls) && otherUser.photo_urls.length > 0
                ? otherUser.photo_urls[0]
                : (otherUser.avatar_url || otherUser.avatar || userAvatar);
            }
          }

          const newConversation: any = {
            id: newMessage.chat_id,
            lastMessage: newMessage.content || newMessage.text || 'New message',
            timestamp: newMessage.created_at || new Date().toISOString(),
            updated_at: newMessage.created_at || new Date().toISOString(),
            created_at: newMessage.created_at || new Date().toISOString(),
            // V168.LOADING: Use fetched user data
            name: userName,
            photoUrl: userAvatar,
            otherUserId: newMessage.otherUserId || null,
            ...(newMessage.user1_id ? { user1_id: newMessage.user1_id } : {}),
            ...(newMessage.user2_id ? { user2_id: newMessage.user2_id } : {}),
            unreadCount: 0,
            isOnline: false
          };

          // Add the new conversation and sort
          const withNewConv = [...prevArray, newConversation];
          // VCHAT-FINAL-04: Sort copy to prevent mutation bugs
          const sorted = [...withNewConv].sort((a, b) => {
            const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
            const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
            return timeB - timeA;
          });

          void loadConversations(true);

          console.log('✅ V167.7: Created placeholder conversation, total conversations:', sorted.length);
          return sorted;
        }

        const updated = prevArray.map(conv => {
          if (conv.id === newMessage.chat_id) {
            const newTimestamp = new Date(newMessage.created_at || new Date()).getTime();
            const oldTimestamp = new Date(conv.timestamp || conv.created_at || 0).getTime();

            // 🚀 V167: CRITICAL FIX - Always update for own messages OR if server message is newer
            // This ensures chat list updates immediately when messages are sent
            if (newMessage.sender_id === user.id || newTimestamp >= oldTimestamp) {
              console.log('✅ V167: Updating conversation via chat-message-inserted event:', {
                chatId: newMessage.chat_id,
                lastMessage: newMessage.content,
                timestamp: newMessage.created_at,
                isOwnMessage: newMessage.sender_id === user.id,
                oldLastMessage: conv.lastMessage,
                oldTimestamp: conv.timestamp
              });
              return {
                ...conv,
                lastMessage: newMessage.content || newMessage.text || conv.lastMessage,
                timestamp: newMessage.created_at || newMessage.timestamp || conv.timestamp,
                updated_at: newMessage.created_at || newMessage.timestamp || conv.updated_at
              };
            } else {
              console.log('⏭️ V167: Skipping update - message is older:', {
                chatId: newMessage.chat_id,
                newTimestamp,
                oldTimestamp
              });
            }
          }
          return conv;
        });

        // Sort by timestamp (most recent first) - ensure latest message is at top
        const sorted = [...updated].sort((a, b) => {
          const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
          const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
          return timeB - timeA;
        });

        console.log('✅ V167: Conversation list updated, sorted conversations:', {
          totalConversations: sorted.length,
          topConversation: sorted[0] ? {
            id: sorted[0].id,
            lastMessage: sorted[0].lastMessage,
            timestamp: sorted[0].timestamp
          } : null
        });

        return sorted;
      });
    };

    // 🚀 V167: Listen for chat-updated events to update conversations when chat is updated
    const handleChatUpdated = (evt: any) => {
      const { chatId, lastMessage, lastMessageAt, updatedAt } = evt?.detail || {};
      if (!chatId || !user?.id) return;

      console.log('🔔 V167: Chat updated event received:', {
        chatId,
        lastMessage,
        lastMessageAt
      });

      // Update conversation in list immediately
      setConversations(prev => {
        const updated = prev.map(conv => {
          if (conv.id === chatId) {
            return {
              ...conv,
              lastMessage: lastMessage || conv.lastMessage,
              timestamp: lastMessageAt || updatedAt || conv.timestamp,
              updated_at: updatedAt || conv.updated_at
            };
          }
          return conv;
        });

        // Sort by timestamp (most recent first)
        return [...updated].sort((a, b) => {
          const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
          const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
          return timeB - timeA;
        });
      });
    };

    // Also listen for force refresh events
    const handleForceRefresh = (evt: any) => {
      const {
        chatId,
        lastMessage,
        timestamp,
        otherUserName,
        otherUserAvatar,
        otherUserId,
        user1_id: detailUser1Id,
        user2_id: detailUser2Id,
        reason,
        userId
      } = evt?.detail || {};

      // V168.BLOCK: Handle user blocked event - immediately remove chats with blocked user
      if (reason === 'user_blocked' && userId && user?.id) {
        console.log('🔒 BLOCK: Removing chats with blocked user:', userId);

        setConversations(prev => {
          // Remove all conversations where the other user is the blocked user
          const filtered = prev.filter(conv => {
            const otherUserId = conv.user1_id === user.id ? conv.user2_id : conv.user1_id;
            const shouldRemove = otherUserId === userId;
            if (shouldRemove) {
              console.log('🔒 BLOCK: Removing chat:', conv.id, 'with blocked user:', userId);
            }
            return !shouldRemove;
          });

          console.log('✅ BLOCK: Removed chats, remaining conversations:', filtered.length);
          return filtered;
        });

        void loadConversations(true);

        return; // Don't process as normal refresh event
      }

      if (!chatId || !user?.id) return;

      console.log('🔔 V167.7: Force refresh event received:', { chatId, lastMessage, timestamp, otherUserName });

      // 🚀 V167.7: CRITICAL FIX - Check if conversation exists, if not, create placeholder
      setConversations(prev => {
        const conversationExists = prev.some(conv => conv.id === chatId);

        if (!conversationExists) {
          // 🚀 V167.7: CRITICAL FIX - Create conversation entry immediately
          console.log('🚀 V167.7: Conversation not found, creating placeholder entry:', chatId);

          // V168.LOADING: Try to get user data immediately
          let userName = otherUserName || null; // V329.2: Use null so ChatList can distinguish "loading" from "resolved"
          let userAvatar = otherUserAvatar || null;

          if (getUser && otherUserId) {
            const otherUser = getUser(otherUserId);
            if (otherUser) {
              userName = otherUser.name || userName;
              // photo_urls is an array, get first element
              userAvatar = Array.isArray(otherUser.photo_urls) && otherUser.photo_urls.length > 0
                ? otherUser.photo_urls[0]
                : (otherUser.avatar_url || otherUser.avatar || userAvatar);
            }
          }

          const newConversation: any = {
            id: chatId,
            lastMessage: lastMessage || 'New message',
            timestamp: timestamp || new Date().toISOString(),
            updated_at: timestamp || new Date().toISOString(),
            created_at: timestamp || new Date().toISOString(),
            // V168.LOADING: Use fetched user data
            name: userName,
            photoUrl: userAvatar,
            otherUserId: otherUserId || null,
            ...(detailUser1Id ? { user1_id: detailUser1Id } : {}),
            ...(detailUser2Id ? { user2_id: detailUser2Id } : {}),
            unreadCount: 0,
            isOnline: false
          };

          // Add the new conversation and sort
          const withNewConv = [...prev, newConversation];
          // VCHAT-FINAL-04: Sort copy to prevent mutation bugs
          const sorted = [...withNewConv].sort((a, b) => {
            const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
            const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
            return timeB - timeA;
          });

          void loadConversations(true);

          console.log('✅ V167.7: Created placeholder conversation, total conversations:', sorted.length);
          return sorted;
        }

        // Conversation exists - update it (merge identity fields when missing)
        const updated = prev.map(conv => {
          if (conv.id === chatId) {
            const next: any = {
              ...conv,
              lastMessage: lastMessage ?? conv.lastMessage,
              timestamp: timestamp || conv.timestamp || new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            if (otherUserId && !conv.otherUserId) next.otherUserId = otherUserId;
            if (detailUser1Id && !conv.user1_id) next.user1_id = detailUser1Id;
            if (detailUser2Id && !conv.user2_id) next.user2_id = detailUser2Id;
            if (otherUserName && !hasResolvedDisplayName(conv)) next.name = otherUserName;
            if (otherUserAvatar && !conv.photoUrl) next.photoUrl = otherUserAvatar;
            return next;
          }
          return conv;
        });

        // Sort by timestamp
        return [...updated].sort((a, b) => {
          const timeA = new Date(a.timestamp || a.updated_at || a.created_at || 0).getTime();
          const timeB = new Date(b.timestamp || b.updated_at || b.created_at || 0).getTime();
          return timeB - timeA;
        });
      });
    };

    // V323: New match received - refresh chat list to show new conversation with correct user details
    const handleNewMatchReceived = () => {
      loadConversations(true);
    };

    window.addEventListener('chat-message-inserted' as any, handleMessageInserted as any);
    window.addEventListener('force-chatlist-refresh' as any, handleForceRefresh as any);
    window.addEventListener('chat-updated' as any, handleChatUpdated as any);
    window.addEventListener('blissnet-new-match-received' as any, handleNewMatchReceived);

    return () => {
      window.removeEventListener('chat-message-inserted' as any, handleMessageInserted as any);
      window.removeEventListener('force-chatlist-refresh' as any, handleForceRefresh as any);
      window.removeEventListener('chat-updated' as any, handleChatUpdated as any);
      window.removeEventListener('blissnet-new-match-received' as any, handleNewMatchReceived);
    };
  }, [user?.id, loadConversations, getUser]);

  useEffect(() => {
    if (!user?.id) {
      conversationsHydratedRef.current = false;
      setConversationsHydrated(false);
      return;
    }
    // Only re-run on user id — loadConversations identity churn (getUser/network hydrate)
    // must not trigger a second full load that used to flip loadingConversations.
    void loadConversationsRef.current();
  }, [user?.id]);

  useEffect(() => {
    return () => {
      Object.values(subscriptionsRef.current).forEach(channel => {
        if (channel) supabase.removeChannel(channel);
      });
      subscriptionsRef.current = {};
    };
  }, [user?.id]);

  useEffect(() => {
    const timeouts = typingTimeouts.current;
    return () => {
      Object.values(subscriptionsRef.current).forEach(channel => {
        if (channel) supabase.removeChannel(channel);
      });
      subscriptionsRef.current = {};

      Object.values(timeouts).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, []);

  const value: MessagesDataContextType = {
    conversations,
    loadingConversations,
    conversationsHydrated,
    errorConversations,
    loadConversations,
    getMessages,
    loadMessages,
    sendMessage,
    getUnreadCount,
    markAsRead,
    totalUnreadCount,
    subscribeToChat,
    unsubscribeFromChat,
    typingUsers,
    setUserTyping,
    setCurrentChatId
  };

  return (
    <MessagesDataContext.Provider value={value}>
      {children}
    </MessagesDataContext.Provider>
  );
};
